import type { TabItem } from '@components/shared/DraggableTabs/types';
import type { FormAccessLevel } from '@/types/form';
import type { FormTemplateTab } from '@/types/formTemplate';
import type { FilterValues } from '@/types/filters';

/** Any tab type that has a _tabClientId and name */
interface TabWithClientId {
  _tabClientId: string;
  name: string;
}

/** Map client tabs to DraggableTabs TabItem array */
export function toDraggableTabItems<T extends TabWithClientId>(tabs: T[]): TabItem[] {
  return tabs.map((tab) => ({
    id: tab._tabClientId,
    label: tab.name || 'Untitled Tab',
  }));
}

/** Get details about removed tabs for confirmation modal display */
export function getRemovedTabDetails<T extends TabWithClientId & { forms: Array<{ formName?: string; formConfigId: string }> }>(
  tabs: T[],
  removedTabIds: Set<string>,
): Array<{ name: string; formNames: string }> {
  return tabs
    .filter((t) => removedTabIds.has(t._tabClientId))
    .map((t) => ({
      name: t.name || 'Untitled Tab',
      formNames: t.forms.map((f) => f.formName || f.formConfigId).join(', '),
    }));
}

/** Standard URL search params shape for DataTable pages */
export interface TableSearchParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** JSON-serialized FilterValues from the filter panel */
  filters?: string;
  /** Status tab selection — drives top-level status filter */
  tab?: string;
  /** Free-text search string from the toolbar search input */
  search?: string;
  /** Rendering view — 'table' (default) or 'card'. Supported by DataTable but not currently used. */
  view?: 'table' | 'card';
}

/** Optional default sort/tab values for tables */
interface TableSearchDefaults {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  tab?: string;
}

/** Validate and extract table search params from URL search object */
export function validateTableSearch(
  search: Record<string, unknown>,
  defaults?: TableSearchDefaults,
): TableSearchParams {
  return {
    page: typeof search.page === 'number' ? search.page : undefined,
    size: typeof search.size === 'number' ? search.size : undefined,
    sortBy: typeof search.sortBy === 'string' ? search.sortBy : defaults?.sortBy,
    sortDir: search.sortDir === 'asc' || search.sortDir === 'desc' ? search.sortDir : defaults?.sortDir,
    filters: typeof search.filters === 'string' ? search.filters : undefined,
    tab: typeof search.tab === 'string' ? search.tab : defaults?.tab,
    search: typeof search.search === 'string' ? search.search : undefined,
    view: search.view === 'card' ? 'card' : undefined,
  };
}

// ============================================
// FILTER SERIALIZATION
// ============================================

/** Serialize FilterValues to a URL-safe JSON string. Returns undefined if empty. */
export function serializeFilters(filters: FilterValues): string | undefined {
  const keys = Object.keys(filters);
  if (keys.length === 0) return undefined;
  return JSON.stringify(filters);
}

/** Deserialize filters from URL search param string. Returns empty object on invalid input. */
export function deserializeFilters(raw: unknown): FilterValues {
  if (typeof raw !== 'string' || raw === '') return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as FilterValues;
    }
    return {};
  } catch {
    return {};
  }
}

/** Build Spring Pageable sort string from sort state and column definitions */
export function buildSortString(
  sort: { columnId: string; direction: string } | undefined,
  columns: Array<{ id: string; sortKey?: string }>,
): string | undefined {
  if (!sort) return undefined;
  const col = columns.find((c) => c.id === sort.columnId);
  return `${col?.sortKey ?? sort.columnId},${sort.direction}`;
}

/** Build pagination state from API pageable response */
export function toPaginationState(data: {
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
} | undefined) {
  if (!data) return undefined;
  return {
    page: data.number,
    size: data.size,
    totalElements: data.totalElements,
    totalPages: data.totalPages,
  };
}

/** Convert resolved/client tabs to API FormTemplateTab format, stripping client IDs */
export function toApiTabsFromResolved(
  resolvedTabs: Array<{
    name: string;
    description?: string;
    forms: Array<{
      formConfigId: string;
      formName?: string;
      accessLevel: FormAccessLevel;
      isDefault: boolean;
    }>;
  }>,
): FormTemplateTab[] {
  return resolvedTabs.map((tab, i) => ({
    name: tab.name,
    description: tab.description ?? '',
    order: i + 1,
    forms: tab.forms.map((form, fi) => ({
      formConfigId: form.formConfigId,
      formName: form.formName,
      order: fi + 1,
      accessLevel: form.accessLevel,
      filters: {
        country: { matchAll: true, values: [] },
        productType: { matchAll: true, values: [] },
      },
      attributes: {
        isDefault: form.isDefault,
      },
    })),
  }));
}
